<template>
  <div>
    <page-header
      title="流水核销"
      :crumbs="['投后管理', '流水核销']"
      :subtitle="`共 ${pagination.total} 笔银行流水`"
    />

    <DataTable
      :columns="columns"
      :data="bankTransLogs"
      :total="pagination.total"
      :page="pagination.pageNum"
      :page-size="pagination.pageSize"
      row-key="serialId"
      :ops-width="140"
      empty-text="暂无银行流水"
      @update:page="handleCurrentChange"
      @update:page-size="handleSizeChange"
      @refresh="getBankTransLogs"
    >
      <template #cell-transTime="{ row }">
        {{ formatDate(row.transTime) }}
      </template>

      <template #cell-status="{ row }">
        <span class="tag" :class="statusClass(row.status)">{{ statusText(row.status) }}</span>
      </template>

      <template #ops="{ row }">
        <button
          class="dt-op"
          :disabled="row.availableAmount <= 0 || row.status === 'IGNORED'"
          @click="handleWriteOff(row)"
        >核销</button>
        <span class="dt-sep">·</span>
        <button
          class="dt-op is-danger"
          :disabled="row.status === 'IGNORED' || row.status === 'CONSUMED'"
          @click="handleIgnore(row)"
        >忽略</button>
      </template>
    </DataTable>

    <!-- 核销对话框 -->
    <el-dialog v-model="writeOffDialogVisible" title="手动核销" width="560px">
      <el-form :model="writeOffForm" label-position="top">
        <el-form-item label="流水号">
          <el-input v-model="writeOffForm.serialId" disabled />
        </el-form-item>
        <el-form-item label="可用金额">
          <el-input v-model="writeOffForm.availableAmount" disabled />
        </el-form-item>
        <el-form-item label="选择品牌">
          <el-select
            v-model="writeOffForm.brandCode"
            placeholder="请选择品牌"
            clearable
            style="width: 100%"
            @change="handleBrandChange"
          >
            <el-option v-for="b in brandList" :key="b.brandCode" :label="b.brandName" :value="b.brandCode" />
          </el-select>
        </el-form-item>
        <el-form-item label="选择门店">
          <el-select
            v-model="writeOffForm.storeEntCodes"
            placeholder="请选择门店（可多选）"
            multiple
            collapse-tags
            collapse-tags-tooltip
            :disabled="!writeOffForm.brandCode"
            style="width: 100%"
          >
            <el-option v-for="s in filteredStoreList" :key="s.storeEntCode" :label="s.storeName" :value="s.storeEntCode" />
          </el-select>
        </el-form-item>
        <div class="form-row">
          <el-form-item label="核销开始日期">
            <el-date-picker v-model="writeOffForm.startDate" type="date" placeholder="可选" value-format="YYYY-MM-DD" style="width: 100%" />
          </el-form-item>
          <el-form-item label="核销截止日期">
            <el-date-picker v-model="writeOffForm.endDate" type="date" placeholder="请选择" value-format="YYYY-MM-DD" style="width: 100%" />
          </el-form-item>
        </div>
        <el-form-item label="待核销金额">
          <el-input v-model="writeOffForm.pendingAmount" disabled />
        </el-form-item>
      </el-form>
      <template #footer>
        <button class="btn" @click="writeOffDialogVisible = false">取消</button>
        <button class="btn btn-primary" @click="confirmWriteOff">确认核销</button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import PageHeader from '@/components/PageHeader.vue'
import DataTable from '@/components/DataTable.vue'
import postInvestService from '@/services/postInvestService'
import assetService from '@/services/assetService'

const bankTransLogs = ref([])
const pagination = reactive({ pageNum: 1, pageSize: 20, total: 0 })

const writeOffDialogVisible = ref(false)
const writeOffForm = reactive({
  serialId: '',
  availableAmount: 0,
  brandCode: '',
  storeEntCodes: [],
  startDate: '',
  endDate: '',
  pendingAmount: 0
})

const brandList = ref([])
const storeList = ref([])

const columns = [
  { prop: 'serialId', label: '流水号', minWidth: 190 },
  { prop: 'transTime', label: '交易时间', minWidth: 150 },
  { prop: 'amount', label: '交易金额', width: 130, type: 'money' },
  { prop: 'availableAmount', label: '可用金额', width: 130, type: 'money' },
  { prop: 'payer', label: '付款方账号', minWidth: 170 },
  { prop: 'payerName', label: '付款方名称', minWidth: 160 },
  { prop: 'status', label: '状态', width: 100 }
]

const filteredStoreList = computed(() =>
  writeOffForm.brandCode ? storeList.value.filter(s => s.brandCode === writeOffForm.brandCode) : []
)

const statusText = (status) => ({
  RECEIVED: '未核销', PARTIAL: '部分核销', CONSUMED: '已核销', IGNORED: '已忽略'
}[status] || status)

const statusClass = (status) => ({
  RECEIVED: 'tag-warning',
  PARTIAL: 'tag-info',
  CONSUMED: 'tag-success',
  IGNORED: 'tag-neutral'
}[status] || 'tag-neutral')

const getBankTransLogs = async () => {
  try {
    const res = await postInvestService.getBankTransLogs({
      pageNum: pagination.pageNum,
      pageSize: pagination.pageSize
    })
    if (res?.code === 200) {
      bankTransLogs.value = res.data?.list || []
      pagination.total = res.data?.total || 0
    } else {
      ElMessage.error(res?.msg || '获取银行流水失败')
    }
  } catch {
    ElMessage.error('获取银行流水失败')
  }
}

const getBrandList = async () => {
  try {
    const res = await assetService.getBrandList({})
    brandList.value = res?.data?.list || res?.data || []
  } catch {
    // 品牌列表失败不阻断主流程
  }
}

const getStoreList = async () => {
  try {
    const res = await assetService.getStoreList({})
    storeList.value = res?.data?.storeList || []
  } catch {
    // 门店列表失败不阻断主流程
  }
}

const handleBrandChange = () => {
  writeOffForm.storeEntCodes = []
  writeOffForm.pendingAmount = 0
}

const handleWriteOff = (row) => {
  Object.assign(writeOffForm, {
    serialId: row.serialId,
    availableAmount: row.availableAmount,
    brandCode: '',
    storeEntCodes: [],
    startDate: '',
    endDate: '',
    pendingAmount: 0
  })
  writeOffDialogVisible.value = true
}

const handleIgnore = (row) => {
  ElMessageBox.confirm('确定要忽略这条流水吗？忽略后将不再参与核销。', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      const res = await postInvestService.ignoreBankTransLog({ serialId: row.serialId })
      if (res?.code === 200) {
        ElMessage.success('忽略成功')
        getBankTransLogs()
      } else {
        ElMessage.error(res?.msg || '忽略失败')
      }
    } catch {
      ElMessage.error('忽略失败')
    }
  }).catch(() => {})
}

const getPendingAmount = async () => {
  if (!writeOffForm.storeEntCodes.length || !writeOffForm.endDate) {
    writeOffForm.pendingAmount = 0
    return
  }
  try {
    const res = await postInvestService.getPendingAmount({
      storeEntCodes: writeOffForm.storeEntCodes,
      startDate: writeOffForm.startDate,
      endDate: writeOffForm.endDate
    })
    writeOffForm.pendingAmount = res?.code === 200 ? (res.data || 0) : 0
  } catch {
    writeOffForm.pendingAmount = 0
  }
}

const confirmWriteOff = async () => {
  if (!writeOffForm.brandCode) return ElMessage.error('请选择品牌')
  if (!writeOffForm.storeEntCodes.length) return ElMessage.error('请选择门店')
  if (!writeOffForm.endDate) return ElMessage.error('请选择核销截止日期')
  if (writeOffForm.pendingAmount <= 0) return ElMessage.error('没有待核销的金额')
  if (writeOffForm.pendingAmount > writeOffForm.availableAmount) {
    return ElMessage.error('待核销金额大于可用金额，请确认银行流水金额是否正确')
  }

  try {
    const res = await postInvestService.writeOffBankTransLog({
      serialId: writeOffForm.serialId,
      brandCode: writeOffForm.brandCode,
      storeEntCodes: writeOffForm.storeEntCodes,
      startDate: writeOffForm.startDate,
      endDate: writeOffForm.endDate
    })
    if (res?.code === 200) {
      ElMessage.success('核销成功')
      writeOffDialogVisible.value = false
      getBankTransLogs()
    } else {
      ElMessage.error(res?.msg || '核销失败')
    }
  } catch {
    ElMessage.error('核销失败')
  }
}

const handleSizeChange = (size) => {
  pagination.pageSize = size
  pagination.pageNum = 1
  getBankTransLogs()
}

const handleCurrentChange = (current) => {
  pagination.pageNum = current
  getBankTransLogs()
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  })
}

onMounted(async () => {
  await getBankTransLogs()
  getBrandList()
  getStoreList()
})

watch(
  () => [writeOffForm.storeEntCodes, writeOffForm.startDate, writeOffForm.endDate],
  () => getPendingAmount(),
  { deep: true }
)
</script>

<style scoped>
/* 状态标签：沿用设计系统语义色 */
.tag {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: var(--radius-tag);
  white-space: nowrap;
}

.tag-success { background: var(--success-bg); color: var(--success-text); }
.tag-warning { background: var(--warning-bg); color: var(--warning-text); }
.tag-danger  { background: var(--danger-bg);  color: var(--danger-text); }
.tag-info    { background: var(--info-bg);    color: var(--info-text); }
.tag-neutral { background: var(--bg-page);    color: var(--text-secondary); }

.form-row {
  display: flex;
  gap: 12px;
}

.form-row > * {
  flex: 1;
  min-width: 0;
}

.btn {
  padding: 6px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}

.btn:hover {
  border-color: var(--brand-500);
  color: var(--brand-500);
}

.btn-primary {
  background: var(--brand-500);
  border-color: var(--brand-500);
  color: #fff;
}

.btn-primary:hover {
  background: var(--brand-600);
  border-color: var(--brand-600);
  color: #fff;
}
</style>
